/**
 * Engagements — the client roster for recurring service work.
 *
 * One row per client per service line. An engagement is the thing that produces
 * a cycle each period; it is not a project and has no completion percentage.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Engagement, type ServiceLine } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Search, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EngagementFormDialog } from './engagement-form-dialog';

export function EngagementsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [lineId, setLineId] = useState('');
  const [creating, setCreating] = useState(false);

  const { data: linesData } = useQuery({
    queryKey: ['svc', 'service-lines'],
    queryFn: () => api.getServiceLines(),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'engagements', search, lineId],
    queryFn: () => api.getEngagements({ search: search || undefined, serviceLineId: lineId || undefined }),
  });

  const lines = (linesData?.data ?? []) as ServiceLine[];
  const rows = (data?.data ?? []) as Engagement[];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold tracking-tight">Engagements</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Clients on a recurring service plan.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New engagement
        </Button>
      </div>

      <EngagementFormDialog open={creating} onOpenChange={setCreating} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8 w-64" placeholder="Search clients…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5">
          <FilterChip active={lineId === ''} onClick={() => setLineId('')}>All</FilterChip>
          {lines.map((l) => (
            <FilterChip key={l.id} active={lineId === l.id} onClick={() => setLineId(l.id)}>
              {l.name}
            </FilterChip>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No engagements yet. Create a service plan first, then attach a client to it.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Current cycle</TableHead>
                  <TableHead className="text-right">Monthly</TableHead>
                  <TableHead>Next cycle</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => (
                  <TableRow
                    key={e.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/service/engagements/${e.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{e.account_name}</div>
                      <div className="text-xs text-muted-foreground">#{e.engagement_number}</div>
                    </TableCell>
                    <TableCell className="text-sm">{e.service_line_name}</TableCell>
                    <TableCell className="text-sm">
                      {e.plan_name ?? <span className="text-muted-foreground">none</span>}
                      {e.plan_version != null && e.plan_name && (
                        <span className="text-xs text-muted-foreground ml-1">v{e.plan_version}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {e.latest_cycle_label ?? '—'}
                      {(e.overdue_work_items ?? 0) > 0 && (
                        <Badge variant="destructive" className="ml-2">
                          {e.overdue_work_items} overdue
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {e.monthly_price != null ? formatCurrency(e.monthly_price) : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {e.next_cycle_date ? formatDate(e.next_cycle_date) : '—'}
                      {!e.auto_generate && (
                        <span className="ml-1 text-xs text-muted-foreground">(manual)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.status === 'active' ? 'default' : 'secondary'}>
                        {e.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FilterChip({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
        active ? 'bg-primary text-primary-foreground border-primary'
               : 'bg-background hover:bg-muted border-input'
      }`}
    >
      {children}
    </button>
  );
}
